import { useContext } from 'react';
import { MainContext } from 'Douleutaras/layout/DefaultFullLayout';
import { isRealProduction } from 'Douleutaras/constants/system';

// For now this is only event tracking type we going to have.
// It could take only one parameter and one value
// which will cover 100% of our current needs.
export enum AnalyticsEventTypes {
  EVENT_TRACKING = 'eventTracking',
  PROJECTED_REVENUE = 'projectedRevenue',
  PROJECTED_REVENUE_APP = 'projectedRevenueApp',
  DIRECT_PURCHASE = 'directPurchase',
  DIRECT_PURCHASE_APP = 'directPurchaseApp',
  NORMAL_PURCHASE = 'purchase',
  NORMAL_PURCHASE_APP = 'purchaseApp',
  SET_DLV_VARIABLE = 'setDlvVariable'
}

/**
 * How does this work:
 * The main idea is to transfer all data through the dataLayer,
 * which is the standard data transfer mechanism in GTM and GA.
 *
 * We have several tags in gtm that are responsible for receiving
 * this data and transmitting it to the GA:
 * - CE - eventTracking [GA4]
 * - GA4 - Event Tracking
 * - DLV - Event Category
 * - DLV - Event Action
 * - DLV - Event Label
 *
 * CE - eventTracking [GA4]: This is Custom event tag which listening
 * for special event name "eventTracking" (check AnalyticsEventTypes enum).
 * when such an event occurs it triggers the tag "GA4 - Event Tracking".
 *
 * GA4 - Event Tracking: This tag collects data from dataLayer and passes it to the GA.
 * To obtain data, we use pre-created variables (DLV - Event Category, DLV - Event Action, DLV - Event Label)
 * that receive data from our global dataLayer object.
 *
 * Thus, it turns out that we need to transmit data in a strict order and strictly follow the name of the event
 * that is monitored by the GTM. This is exactly what this service helps us do.
 * All you need to do is call the desired method with the data you want to pass.
 * Here's an example:
 *
 * const { track } = useGtm();
 *
 * const handleClick = () => {
 *   track('pros profile - cta clicked', 'Button name', 'Rebook');
 * };
 *
 * After we call this method, the GTM will see this and transmit the following data to the GA:
 *
 * DLV - Event Category (which is basically Event name): "pros profile - cta clicked"
 * DLV - Event Action (which is basically Event property name): "Button name"
 * DLV - Event Label (which is basically Event property value): "Rebook"
 *
 * And one more thing. You can dispatch the same thing inside the GTM itself. Just use this helper tag:
 * - CJ - Tracking Helpers
 *
 * Example:
 * var Helpers = {{CJ - Tracking Helpers}};
 *
 * Helpers.sendEvent('pros profile - cta clicked', 'Button name', 'Rebook');
 *
 */

export const useGtm = () => {

  const analytics = useContext(MainContext);

  const cloned = {
    ...analytics.analytics
  };

  const log = (...identifiers) => {
    console.group('%cGTM event log:', 'background: #1FC198; color: #FFFFFF');
    // tslint:disable-next-line:no-console
    console.log(...identifiers);
    console.groupEnd();
  };

  const eventOptions = 'background: #FBE8C2; color: #656565';

  return {
    track: (eventCategory, eventAction = 'Action', eventLabel = undefined) =>
      isRealProduction
        ? cloned.track(AnalyticsEventTypes.EVENT_TRACKING, {
            eventCategory,
            eventAction,
            eventLabel
          })
        // tslint:disable-next-line:no-console
        : log(`%c${eventCategory}`, eventOptions, eventAction, eventLabel),

    trackPlain: (customEvent, data) =>
      isRealProduction
        ? cloned.track(customEvent, data)
        // tslint:disable-next-line:no-console
        : log(`%cTracked: ${customEvent}`, eventOptions, data),

    trackRevenue: (projectedRevenue, isApp = false) =>
      isRealProduction
        ? cloned.track(isApp
          ? AnalyticsEventTypes.PROJECTED_REVENUE_APP
          : AnalyticsEventTypes.PROJECTED_REVENUE, { projectedRevenue })
        // tslint:disable-next-line:no-console
        : log('%cProjected revenue: ', eventOptions, projectedRevenue)
  };
};

export default useGtm;
